import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/auth/AuthProvider";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Loader2,
  Trophy,
  Zap,
  Star,
  CheckCircle2,
  PlayCircle,
  Megaphone,
  ListChecks,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/student/")({
  component: StudentHome,
});

interface Lesson {
  id: string;
  course_id: string;
  title: string;
  description: string | null;
  sort_order: number;
  points_reward: number;
}
interface Course {
  id: string;
  name: string;
  symbol: string | null;
}
interface Quiz {
  id: string;
  lesson_id: string | null;
  title: string;
  points_reward: number;
}
interface MyBadge {
  earned_at: string;
  badges: {
    name: string;
    icon: string | null;
    color: string | null;
    description: string | null;
  } | null;
}
interface Announcement {
  id: string;
  title: string;
  content: string;
  created_at: string;
}

function StudentHome() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<{
    full_name: string | null;
    points: number;
    level: number;
  } | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [completed, setCompleted] = useState<Set<string>>(new Set());
  const [attempts, setAttempts] = useState<Map<string, number>>(new Map());
  const [badges, setBadges] = useState<MyBadge[]>([]);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);

  useEffect(() => {
    let isMounted = true;
    if (!user) return;
    (async () => {
      const prRes = supabase
        .from("profiles")
        .select("full_name,points,level")
        .eq("id", user.id)
        .maybeSingle();
      const gmRes = supabase
        .from("group_members")
        .select("group_id, groups(course_id)")
        .eq("student_id", user.id);
      const lsRes = supabase
        .from("lessons")
        .select("id,course_id,title,description,sort_order,points_reward")
        .order("sort_order");
      const qzRes = supabase.from("quizzes").select("id,lesson_id,title,points_reward");
      const lpRes = supabase.from("lesson_progress").select("lesson_id").eq("student_id", user.id);
      const qaRes = supabase
        .from("quiz_attempts")
        .select("quiz_id,percentage")
        .eq("student_id", user.id);
      const sbRes = supabase
        .from("student_badges")
        .select("earned_at, badges(name,icon,color,description)")
        .eq("student_id", user.id);

      const [pr, gm, ls, qz, lp, qa, sb] = await Promise.all([
        prRes,
        gmRes,
        lsRes,
        qzRes,
        lpRes,
        qaRes,
        sbRes,
      ]);

      const groupIds = (gm.data ?? []).map((r) => r.group_id);
      const an = await supabase
        .from("announcements")
        .select("id,title,content,created_at")
        .in("group_id", groupIds.length ? groupIds : ["__none__"])
        .order("created_at", { ascending: false })
        .limit(5);

      if (!isMounted) return;

      setProfile(pr.data);
      const courseIds = new Set<string>(
        (gm.data ?? [])
          .map((r) => (r.groups as unknown as { course_id: string } | null)?.course_id)
          .filter(Boolean) as string[],
      );
      const cs = await supabase
        .from("courses")
        .select("id,name,symbol")
        .in("id", Array.from(courseIds.size ? courseIds : ["__none__"]));

      if (!isMounted) return;

      setCourses((cs.data ?? []) as Course[]);
      setLessons(
        ((ls.data ?? []) as Lesson[]).filter(
          (l) => courseIds.has(l.course_id) || courseIds.size === 0,
        ),
      );
      setQuizzes((qz.data ?? []) as Quiz[]);
      setCompleted(new Set((lp.data ?? []).map((r) => r.lesson_id as string)));
      const m = new Map<string, number>();
      (qa.data ?? []).forEach((a) => {
        const cur = m.get(a.quiz_id as string) ?? 0;
        if ((a.percentage as number) > cur) m.set(a.quiz_id as string, a.percentage as number);
      });
      setAttempts(m);
      setBadges((sb.data ?? []) as unknown as MyBadge[]);
      setAnnouncements((an.data ?? []) as Announcement[]);
      setLoading(false);
    })();

    return () => {
      isMounted = false;
    };
  }, [user]);

  if (loading)
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );

  const totalLessons = lessons.length;
  const doneLessons = lessons.filter((l) => completed.has(l.id)).length;
  const progress = totalLessons ? Math.round((doneLessons / totalLessons) * 100) : 0;
  const totalPoints = profile?.points ?? 0;
  const nextLevelAt = (profile?.level ?? 1) * 100;

  return (
    <div className="container mx-auto px-4 py-10">
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-extrabold">
          أهلاً {profile?.full_name?.split(" ")[0] || "بطل"} 🎮
        </h1>
        <p className="text-muted-foreground mt-1">جاهز للمغامرة النهارده؟</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={<Zap className="h-5 w-5" />}
          label="Cubs Points"
          value={totalPoints}
          color="from-amber-400 to-orange-500"
        />
        <StatCard
          icon={<Star className="h-5 w-5" />}
          label="Level"
          value={profile?.level ?? 1}
          hint={`${totalPoints}/${nextLevelAt}`}
          color="from-violet-500 to-fuchsia-500"
        />
        <StatCard
          icon={<Trophy className="h-5 w-5" />}
          label="Badges"
          value={badges.length}
          color="from-pink-500 to-rose-500"
        />
        <StatCard
          icon={<CheckCircle2 className="h-5 w-5" />}
          label="Lessons"
          value={`${doneLessons}/${totalLessons}`}
          color="from-emerald-500 to-teal-500"
        />
      </div>

      <Card className="rounded-3xl border-0 shadow-soft mb-8 bg-gradient-to-br from-primary/15 to-accent/15">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-lg">Overall Progress</h2>
            <span className="text-2xl font-extrabold text-primary">{progress}%</span>
          </div>
          <Progress value={progress} className="h-3" />
        </CardContent>
      </Card>

      {announcements.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-primary" /> آخر إعلانات الجروب
          </h2>
          <div className="space-y-3">
            {announcements.map((a) => (
              <Card key={a.id} className="rounded-2xl border-0 shadow-soft">
                <CardContent className="p-4">
                  <div className="font-bold">{a.title}</div>
                  <p className="text-sm text-muted-foreground mt-1">{a.content}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      <h2 className="text-2xl font-bold mb-4">📚 الدروس</h2>
      {courses.length === 0 ? (
        <Card className="rounded-3xl border-dashed">
          <CardContent className="p-10 text-center text-muted-foreground">
            لسه مش متسجل في جروب. كلم الإدارة عشان يضيفوك.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {courses.map((c) => {
            const cLessons = lessons.filter((l) => l.course_id === c.id);
            return (
              <div key={c.id}>
                <h3 className="font-bold mb-3 text-lg">
                  {c.symbol} {c.name}
                </h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {cLessons.map((l) => {
                    const done = completed.has(l.id);
                    const lessonQuizzes = quizzes.filter((q) => q.lesson_id === l.id);
                    return (
                      <Card
                        key={l.id}
                        className={`rounded-2xl border-0 shadow-soft transition ${done ? "bg-gradient-to-br from-emerald-500/10 to-teal-500/10" : ""}`}
                      >
                        <CardContent className="p-5">
                          <div className="flex items-start justify-between mb-2">
                            <div className="font-bold flex-1">{l.title}</div>
                            {done && <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />}
                          </div>
                          {l.description && (
                            <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                              {l.description}
                            </p>
                          )}
                          <div className="flex items-center justify-between mt-3">
                            <Badge variant="secondary" className="text-xs">
                              +{l.points_reward} pts
                            </Badge>
                            <div className="flex gap-2">
                              <Button asChild size="sm" variant="outline" className="rounded-full">
                                <Link to="/student/lesson/$lessonId" params={{ lessonId: l.id }}>
                                  <PlayCircle className="h-4 w-4 mr-1" /> عرض الدرس
                                </Link>
                              </Button>
                              {lessonQuizzes.length > 0 && (
                                <Button
                                  asChild
                                  size="sm"
                                  className="rounded-full bg-gradient-primary text-primary-foreground border-0"
                                >
                                  <Link
                                    to="/student/quiz/$quizId"
                                    params={{ quizId: lessonQuizzes[0].id }}
                                  >
                                    <ListChecks className="h-4 w-4 mr-1" />{" "}
                                    {attempts.has(lessonQuizzes[0].id)
                                      ? `${attempts.get(lessonQuizzes[0].id)}%`
                                      : "اختبار"}
                                  </Link>
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                  {cLessons.length === 0 && (
                    <p className="text-sm text-muted-foreground col-span-full">لا توجد دروس بعد</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {badges.length > 0 && (
        <div className="mt-10">
          <h2 className="text-2xl font-bold mb-4">🏆 شاراتي</h2>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
            {badges.map((b, i) => (
              <Card
                key={i}
                className="rounded-2xl border-0 shadow-soft text-center"
                style={{
                  background: `linear-gradient(135deg, ${b.badges?.color}22, ${b.badges?.color}11)`,
                }}
              >
                <CardContent className="p-4">
                  <div className="text-4xl mb-1">{b.badges?.icon || "🏅"}</div>
                  <div className="text-xs font-bold truncate">{b.badges?.name}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  hint,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  hint?: string;
  color: string;
}) {
  return (
    <Card className="rounded-3xl border-0 shadow-soft overflow-hidden">
      <CardContent className="p-5">
        <div
          className={`h-10 w-10 rounded-2xl bg-gradient-to-br ${color} flex items-center justify-center text-white mb-3`}
        >
          {icon}
        </div>
        <div className="text-xs uppercase text-muted-foreground">{label}</div>
        <div className="font-extrabold text-2xl">{value}</div>
        {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
      </CardContent>
    </Card>
  );
}
